import {
  AccountBalanceOutlined,
  AssignmentIndOutlined,
  BuildOutlined,
  CalendarTodayOutlined,
  CategoryOutlined,
  ChangeCircleOutlined,
  CloudUploadOutlined,
  ContentCopy,
  DashboardOutlined,
  DeleteForeverOutlined,
  DriveFileMoveOutlined,
  EmailOutlined,
  EuroOutlined,
  Event,
  EventAvailableOutlined,
  FileOpenOutlined,
  FindInPageOutlined,
  HistoryEduOutlined,
  KeyboardTabOutlined,
  ListOutlined,
  MarkEmailReadOutlined,
  MonetizationOnOutlined,
  NewspaperOutlined,
  NextPlanOutlined,
  NoteAddOutlined,
  PasswordOutlined,
  PauseCircleOutlined,
  PersonAddOutlined,
  PhoneOutlined,
  PlaylistAddCheckOutlined,
  PlaylistAddOutlined,
  PostAddOutlined,
  QuestionAnswerOutlined,
  RestoreOutlined,
  RotateLeft,
  SendOutlined,
  SentimentDissatisfiedOutlined,
  SettingsSuggestOutlined,
  SpeakerNotesOutlined,
  SubjectOutlined,
  UndoOutlined,
} from "@mui/icons-material";
import {
  getCaseLink,
  getIsApplicationComplete,
  getNextStage,
  getProductData,
} from "./Product/ProductService/productService";
import _ from "lodash";
import { buildCalendlyLink } from "./calendlyService";
import { isMobile } from "react-device-detect";
import { userHasOneOfTheseRoles, userHasRole } from "./backofficeUserService";
import { BackofficeUser, Roles } from "../types/BackofficeUser";
import * as caseStatus from "./LeadStatus/StatusCase";
import { isLitigationCase } from "../types/AbstractCase";
import { ActionButtonProps } from "../components/Actions/ActionButton";
import { STAGE_COMPLETED, STAGE_IN_PROGRESS } from "./Stages/StagesCase";
import { isFlexServiceProduct } from "./advocardFlexServiceService";
import { HydraCollectionResult } from "../types/HydraCollectionResult";
import { InquiryToCustomer, InquiryToCustomerStatus } from "../types/InquiryToCustomer";
import { ProductUnion } from "../types/ProductUnion";
import { DrawerContextProps } from "../provider/DrawerProvider";

export const getGeneralActionsByUser = (currentUser: BackofficeUser) => {
  switch (true) {
    case userHasRole(currentUser, Roles.external):
      return [
        {
          icon: DashboardOutlined,
          text: "Übersicht",
          to: "/",
        },
        {
          icon: CalendarTodayOutlined,
          text: "Termine",
          to: "/services/extern/termine",
          isHidden: !currentUser.calendlyId,
        },
        {
          icon: ListOutlined,
          text: "Aufgaben",
          to: "/services/extern/aufgaben",
        },
        {
          icon: PlaylistAddOutlined,
          text: "Ausschreibungen",
          to: "/services/extern/ausgeschriebene-aufgaben",
        },
        {
          icon: NewspaperOutlined,
          text: "Updates",
          to: "/services/extern/release-updates",
        },
      ];
    case userHasRole(currentUser, Roles.callcenter):
      return [
        {
          icon: PersonAddOutlined,
          text: "Neuer Fall",
          to: "/services/callcenter/fall-anlegen",
        },
      ];
    default:
      return [
        {
          icon: ListOutlined,
          text: "Aktivitäten",
          to: "/services/aktivitaeten",
        },
        {
          icon: CalendarTodayOutlined,
          text: "Fristen",
          to: "/services/fristenkalender",
        },
        {
          icon: PersonAddOutlined,
          text: "Neuer Fall",
          to: "/services/tools/mandant-anlegen",
        },
        {
          icon: CategoryOutlined,
          text: "Produkte",
          to: "/services/board/uebersicht",
        },
        {
          icon: BuildOutlined,
          text: "Tools",
          to: "/services/tools",
        },
      ];
  }
};

export const caseRelatedActions = (
  product: ProductUnion,
  setOpen: Function,
  setDrawerState: DrawerContextProps["setDrawerState"],
  currentUser: BackofficeUser,
  hasActiveCall: boolean,
  callModalOpen: boolean,
  openCallModal: Function,
) => {
  //extra list atm since there are actions from the additional and default actions
  if (userHasRole(currentUser, Roles.callcenter)) {
    return [
      {
        text: "E-Mail neu",
        icon: MarkEmailReadOutlined,
        onClick: () => setOpen("resendEmail"),
        isHidden: product.customer.verification !== null,
      },
      {
        text: "Passwort neu",
        icon: PasswordOutlined,
        onClick: () => setOpen("resendEmail"),
        isHidden: product.customer.verification === null,
      },
      {
        text: "Telefonnotiz",
        onClick: () => setOpen("callNotes"),
        icon: SpeakerNotesOutlined,
      },
      {
        text: "Termin buchen",
        icon: EventAvailableOutlined,
        href: buildCalendlyLink(product, true),
        isHidden: !_.startsWith(product.customer.email, "advocard.error"),
      },
    ];
  }

  return [
    {
      text: "Aktivität",
      onClick: () => setDrawerState({ type: "activity" }),
      icon: PlaylistAddCheckOutlined,
      isHidden: currentUser.isExternal,
    },
    {
      text: "Nachricht",
      onClick: () => setDrawerState({ type: "message" }),
      icon: EmailOutlined,
    },
    {
      text: "Anfrage stellen",
      icon: QuestionAnswerOutlined,
      onClick: () => setDrawerState({ type: "inquiryToCustomer" }),
      isHidden: currentUser.isExternal,
    },
    {
      text: "Notiz",
      onClick: () => setDrawerState({ type: "note" }),
      icon: NoteAddOutlined,
    },
    {
      text: "Schriftsatz",
      to: getCaseLink(product) + "/datei-generieren",
      icon: PostAddOutlined,
      isHidden: currentUser.isExternal,
    },
    {
      text: "beA / Fax",
      onClick: () => setDrawerState({ type: "beaFax" }),
      icon: SendOutlined,
      isHidden: currentUser.isExternal,
    },
    {
      text: "Datei",
      onClick: () => setOpen("upload"),
      icon: CloudUploadOutlined,
      divider: !isMobile,
      isHidden: currentUser.isExternal,
    },
    {
      text: "Anruf",
      onClick: () => openCallModal(),
      isHidden: !callModalOpen && !hasActiveCall,
      icon: PhoneOutlined,
      pulsating: hasActiveCall,
    },
    {
      text: "Fallübersicht",
      to: getCaseLink(product, currentUser.isExternal ? "/extern" : ""),
      icon: AssignmentIndOutlined,
    },
    {
      text: "Alle Daten",
      to: getCaseLink(product, currentUser.isExternal ? "/extern" : "") + "/alle-daten",
      icon: SubjectOutlined,
    },
    {
      text: "Konto",
      to: getCaseLink(product) + "/konto",
      icon: AccountBalanceOutlined,
      isHidden: currentUser.isExternal,
    },
    {
      text: "Verlauf",
      to: getCaseLink(product) + "/verlauf",
      icon: RestoreOutlined,
      isHidden: currentUser.isExternal,
      divider: !isMobile,
    },
  ];
};

export const caseRelatedAdditionalActions = (
  product: ProductUnion,
  setOpen: Function,
  inquiryToCustomerCollection: HydraCollectionResult<InquiryToCustomer> | undefined,
  currentUser: BackofficeUser,
): ActionButtonProps[] => [
  {
    text: "Datenauskunft",
    icon: FindInPageOutlined,
    onClick: () => setOpen("requestDataCopy"),
    isHidden:
      product.productClassName !== "dataLeakContract" ||
      product.leadStatus < caseStatus.STATUS_DATA_COMPLETED ||
      product.leadStatus > caseStatus.STATUS_CHECKED,
  },
  {
    text: "Betroffenheit",
    icon: SentimentDissatisfiedOutlined,
    onClick: () => setOpen("requestPersonalAffection"),
    isHidden:
      product.productClassName !== "dataLeakContract" ||
      product.leadStatus < caseStatus.STATUS_DATA_COMPLETED ||
      product.leadStatus > caseStatus.STATUS_TRIAL,
  },
  {
    text: "Ausschreiben",
    icon: MonetizationOnOutlined,
    onClick: () => setOpen("paidTask"),
  },
  {
    text: "E-Mail neu",
    icon: MarkEmailReadOutlined,
    onClick: () => setOpen("resendEmail"),
    isHidden: product.customer.verification !== null,
  },
  {
    text: "Passwort neu",
    icon: PasswordOutlined,
    onClick: () => setOpen("resendEmail"),
    isHidden: product.customer.verification === null,
  },
  {
    text: "Vollmacht neu",
    icon: HistoryEduOutlined,
    onClick: () => setOpen("powerOfAttorney"),
    isHidden:
      Boolean(
        inquiryToCustomerCollection?.["hydra:member"].find(
          (inquiry) =>
            inquiry.identifier === "customerUpdatePoAInquiry" && inquiry.status !== InquiryToCustomerStatus.STATUS_DONE,
        ),
      ) ||
      !(
        ([
          "traffic",
          "divorce",
          "settlement",
          "trafficAccident",
          "rentalContract",
          "generalCivilLaw",
          "civilServiceLaw",
          "employmentContract",
        ].includes(product.productClassName) ||
          (["contractLaw"].includes(product.productCategory) && product.caseCategory !== "advisory")) &&
        product.stage &&
        product.stage >= getProductData(product.productClassName, "setBackCaseStage")
      ),
  },
  {
    text: "Verfahren starten",
    icon: FileOpenOutlined,
    onClick: () => setOpen("createAdministrativeLawCase"),
    isHidden:
      product.productCategory !== "administrativeLaw" ||
      product.administrativeCaseType !== "main" ||
      (product.linkedAdministrativeLawCaseIds["expeditedProceedingCaseId"] &&
        product.linkedAdministrativeLawCaseIds["failureToActLawsuitCaseId"]),
  },
  {
    text: "Pausieren",
    icon: PauseCircleOutlined,
    onClick: () => setOpen("pause"),
  },
  {
    text: "Status ändern",
    icon: NextPlanOutlined,
    onClick: () => setOpen("status"),
  },
  {
    text: "Fall schieben",
    icon: KeyboardTabOutlined,
    onClick: () => setOpen("stage"),
    isHidden: !isMobile || !getNextStage(product),
  },
  {
    text: "Zurücksetzen",
    icon: UndoOutlined,
    onClick: () => setOpen("resetLitigation"),
    isHidden:
      product.caseCategory !== "advisory" ||
      ![STAGE_IN_PROGRESS, STAGE_COMPLETED].includes(product.stage) ||
      isFlexServiceProduct(product),
  },
  {
    text: "Termin buchen",
    icon: EventAvailableOutlined,
    href: buildCalendlyLink(product, true),
    isHidden: !_.startsWith(product.customer.email, "advocard.error"),
  },
  {
    text: "Zahlart ändern",
    icon: EuroOutlined,
    onClick: () => setOpen("privatePayer"),
    isHidden: !getProductData(product.productClassName, "paymentTypeSwitchData").getActionAvailable(product),
  },
  {
    text: "Gerichtstermin",
    icon: Event,
    onClick: () => setOpen("courtAppointment"),
    isHidden: !getProductData(product.productClassName, "courtAppointmentCreationAvailable")(product),
    divider: !isMobile,
  },
  {
    text: "Statuscheck",
    icon: RotateLeft,
    onClick: () => setOpen("statusCheck"),
    isHidden: !getIsApplicationComplete(product) || product.caseCategory === "advisory",
  },
  {
    text: "Neuzuordnung",
    tooltip: "Fall einem anderen Kunden zuordnen",
    icon: DriveFileMoveOutlined,
    onClick: () => setOpen("reassignCase"),
    isHidden: product.leadStatus !== caseStatus.STATUS_CREATED,
  },
  {
    text: "Produkt wechseln",
    icon: ChangeCircleOutlined,
    onClick: () => setOpen("productChange"),
    isHidden: product.productClassName !== "pendingCase",
  },
  {
    text: "Agentenjobs",
    icon: SettingsSuggestOutlined,
    to: getCaseLink(product) + "/execution-jobs",
    isHidden:
      import.meta.env.VITE_DEPLOYMENT === "production" &&
      !userHasOneOfTheseRoles(currentUser, [Roles.admin, Roles.productManager, Roles.developer]),
  },
  {
    text: "Löschen",
    icon: DeleteForeverOutlined,
    onClick: () => setOpen("delete"),
  },
  {
    text: "Import",
    icon: ContentCopy,
    to: getCaseLink(product) + "/dokumente-importieren/vorinstanz",
    isHidden: !isLitigationCase(product) || !product.previousInstanceCase,
  },
  {
    text: "Import",
    icon: ContentCopy,
    to: getCaseLink(product) + "/dokumente-importieren/hauptverfahren",
    isHidden: product.productCategory !== "administrativeLaw" || product.administrativeCaseType === "main",
  },
];
